import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/feature/Navbar';
import { Footer } from '@/components/feature/Footer';
import { useCartContext } from '@/hooks/useCartContext';

export default function CheckoutPage() {
  const { items, subtotal } = useCartContext();
  const [form, setForm] = useState({ name: '', email: '', address: '', city: '', state: '', zip: '', card: '', expiry: '', cvc: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <>
      <Navbar />
      <main className="pt-28 pb-20 bg-stone-50 min-h-screen">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h1 className="text-3xl font-bold text-stone-900 mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Checkout</h1>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-white rounded-2xl p-6">
                <h2 className="font-semibold text-stone-900 mb-4">Shipping Information</h2>
                <div className="space-y-4">
                  {[
                    { name: 'name', label: 'Full Name', placeholder: 'John Smith' },
                    { name: 'email', label: 'Email', placeholder: 'john@example.com' },
                    { name: 'address', label: 'Address', placeholder: '123 Main St' },
                  ].map(f => (
                    <div key={f.name}>
                      <label className="block text-xs font-medium text-stone-500 mb-1">{f.label}</label>
                      <input name={f.name} value={form[f.name as keyof typeof form]} onChange={handleChange}
                        placeholder={f.placeholder}
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
                    </div>
                  ))}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { name: 'city', label: 'City', placeholder: 'Phoenix' },
                      { name: 'state', label: 'State', placeholder: 'AZ' },
                      { name: 'zip', label: 'ZIP', placeholder: '85032' },
                    ].map(f => (
                      <div key={f.name}>
                        <label className="block text-xs font-medium text-stone-500 mb-1">{f.label}</label>
                        <input name={f.name} value={form[f.name as keyof typeof form]} onChange={handleChange}
                          placeholder={f.placeholder}
                          className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-stone-900">Payment</h2>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-shield-check-line text-green-500 text-sm" />
                    <span className="text-xs text-stone-400">Secured by Stripe</span>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <p className="text-amber-700 text-sm font-medium">Stripe Payment Integration</p>
                  <p className="text-amber-600 text-xs mt-1">To enable live Stripe payments, please connect Stripe in the next step. Your customers will be able to pay securely with any credit card.</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1">Card Number</label>
                    <input name="card" value={form.card} onChange={handleChange} placeholder="1234 5678 9012 3456"
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-stone-500 mb-1">Expiry</label>
                      <input name="expiry" value={form.expiry} onChange={handleChange} placeholder="MM/YY"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-500 mb-1">CVC</label>
                      <input name="cvc" value={form.cvc} onChange={handleChange} placeholder="123"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-6 sticky top-28">
                <h2 className="font-semibold text-stone-900 mb-4">Order Summary</h2>
                <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                  {items.map(item => (
                    <div key={item.product.id} className="flex gap-3">
                      <img src={item.product.images[0]} alt={item.product.name} className="w-12 h-12 rounded-lg object-cover object-top flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-stone-900 line-clamp-1">{item.product.name}</p>
                        <p className="text-xs text-stone-400">Qty: {item.quantity}</p>
                      </div>
                      <span className="text-xs font-semibold text-stone-900 whitespace-nowrap">${(item.product.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  {items.length === 0 && <p className="text-stone-400 text-sm text-center py-4">No items in cart</p>}
                </div>
                <div className="border-t border-stone-100 pt-4 space-y-2">
                  <div className="flex justify-between text-sm text-stone-500"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm text-stone-500"><span>Shipping</span><span className="text-green-600">Free</span></div>
                  <div className="flex justify-between font-bold text-stone-900 text-base pt-2 border-t border-stone-100"><span>Total</span><span>${subtotal.toFixed(2)}</span></div>
                </div>
                <button className="w-full mt-6 bg-amber-500 hover:bg-amber-600 text-white py-4 rounded-xl font-semibold transition-colors whitespace-nowrap">
                  Place Order
                </button>
                <Link to="/products" className="block text-center text-stone-400 text-sm mt-3 hover:text-stone-600 transition-colors">
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
